import { createContext, useState, useMemo, useEffect } from "react";
import { createTheme } from "@mui/material/styles";
import { css } from "@emotion/react";

// color design tokens export
export const tokens = (mode) => ({
    ...(mode === "dark"
        ? {
            // New Dark Mode (Account Metrics Style)
            grey: {
                100: "#e6edf7", // --text
                200: "#cfd8dc", // td text
                300: "#90a4ae", // td-small
                400: "#7a8194", // --muted
                500: "#23283b", // --border
                600: "#1b1f2a", // --bg-alt
                700: "#161925", // --bg-elevated
                800: "#0f111a", // --bg
                900: "#050610", // body bg dark end
            },
            primary: {
                100: "#dbeafe",
                200: "#bfdbfe",
                300: "#93c5fd",
                400: "#60a5fa",
                500: "#82aaff", // --primary
                600: "#2563eb",
                700: "#1d4ed8",
                800: "#1e40af",
                900: "#1e3a8a",
            },
            greenAccent: {
                100: "#f0fdf4",
                200: "#dcfce7",
                300: "#bbf7d0",
                400: "#86efac",
                500: "#c3e88d", // --success
                600: "#22c55e", // status indicator
                700: "#15803d",
                800: "#166534",
                900: "#14532d",
            },
            redAccent: {
                100: "#fef2f2",
                200: "#fee2e2",
                300: "#fecaca",
                400: "#fca5a5",
                500: "#ff5370", // --danger
                600: "#ef4444",
                700: "#b91c1c",
                800: "#991b1b",
                900: "#7f1d1d",
            },
            orangeAccent: {
                100: "#fff7ed",
                200: "#ffedd5",
                300: "#fed7aa",
                400: "#fdba74",
                500: "#fb8c00", // Orange
                600: "#ea580c",
                700: "#c2410c",
                800: "#9a3412",
                900: "#7c2d12",
            },
            blueAccent: {
                100: "#e0f2fe",
                200: "#bae6fd",
                300: "#7dd3fc",
                400: "#38bdf8",
                500: "#80cbc4", // --accent (using blueAccent slot for the teal accent to keep structure)
                600: "#0284c7",
                700: "#0369a1",
                800: "#075985",
                900: "#0c4a6e",
            },
            background: {
                default: "#0f111a", // --bg
                paper: "#161925",   // --bg-elevated
                app: "linear-gradient(135deg, rgba(130, 170, 255, 0.08), rgba(11, 13, 21, 0.98))",
                appInner: "radial-gradient(circle at top right, rgba(128, 203, 196, 0.06), rgba(15, 17, 26, 0.98))",
                body: "radial-gradient(circle at top left, #20263a 0, #0b0d15 45%, #050610 100%)",
            }
        }
        : {
            // Light Mode (Mint Theme)
            grey: {
                100: "#0b1220", // --text
                200: "#1f2937", // td text
                300: "#556274", // td-small / secondary text
                400: "#556274", // --muted
                500: "rgba(15, 23, 42, 0.14)", // --border
                600: "#9ca3af",
                700: "#d1d5db",
                800: "#e5e7eb",
                900: "#f3f4f6",
            },
            primary: {
                100: "#ccfbf1",
                200: "#99f6e4",
                300: "#5eead4",
                400: "#2dd4bf",
                500: "#0f766e", // --primary (teal)
                600: "#0d9488",
                700: "#0f766e",
                800: "#115e59",
                900: "#134e4a",
            },
            greenAccent: {
                100: "#dcfce7",
                200: "#bbf7d0",
                300: "#86efac",
                400: "#4ade80",
                500: "#15803d", // --success
                600: "#16a34a",
                700: "#15803d",
                800: "#166534",
                900: "#14532d",
            },
            redAccent: {
                100: "#ffe4e6",
                200: "#fecdd3",
                300: "#fda4af",
                400: "#fb7185",
                500: "#e11d48", // --danger
                600: "#e11d48",
                700: "#be123c",
                800: "#9f1239",
                900: "#881337",
            },
            orangeAccent: {
                100: "#fff7ed",
                200: "#ffedd5",
                300: "#fed7aa",
                400: "#fdba74",
                500: "#fb8c00",
                600: "#ea580c",
                700: "#c2410c",
                800: "#9a3412",
                900: "#7c2d12",
            },
            blueAccent: {
                100: "#dcfce7",
                200: "#bbf7d0",
                300: "#86efac",
                400: "#4ade80",
                500: "#16a34a", // --accent (green) mapped to blueAccent slot for consistency
                600: "#16a34a",
                700: "#15803d",
                800: "#166534",
                900: "#14532d",
            },
            background: {
                default: "#f2f4f7",
                paper: "#ffffff",
                app: "linear-gradient(135deg, rgba(15,118,110,0.08), rgba(255,255,255,0.82))",
                appInner: "radial-gradient(circle at top right, rgba(22,163,74,0.08), rgba(255,255,255,0.92))",
                body: "linear-gradient(to bottom, #D9FFEA, #FEFFE5)",
            }
        }),
});

// mui theme settings
export const themeSettings = (mode, transitionEnabled) => {
    const colors = tokens(mode);
    return {
        palette: {
            mode: mode,
            ...(mode === "dark"
                ? {
                    // palette values for dark mode
                    primary: {
                        main: colors.primary[500],
                    },
                    secondary: {
                        main: colors.blueAccent[500], // Teal accent
                    },
                    neutral: {
                        dark: colors.grey[700],
                        main: colors.grey[500],
                        light: colors.grey[100],
                    },
                    background: {
                        default: colors.background.default,
                        paper: colors.background.paper,
                    },
                    text: {
                        primary: colors.grey[100],
                        secondary: colors.grey[400],
                    },
                }
                : {
                    // palette values for light mode
                    primary: {
                        main: colors.primary[500],
                    },
                    secondary: {
                        main: colors.blueAccent[500],
                    },
                    neutral: {
                        dark: colors.grey[700],
                        main: colors.grey[500],
                        light: colors.grey[100],
                    },
                    background: {
                        default: colors.background.default,
                        paper: colors.background.paper,
                    },
                    text: {
                        primary: colors.grey[100],
                        secondary: colors.grey[300],
                    },
                }),
        },
        typography: {
            fontFamily: ["system-ui", "-apple-system", "BlinkMacSystemFont", '"Segoe UI"', "sans-serif"].join(","),
            fontSize: 12,
            h1: {
                fontFamily: ["system-ui", "-apple-system", "BlinkMacSystemFont", '"Segoe UI"', "sans-serif"].join(","),
                fontSize: 24,
                fontWeight: 700,
                color: colors.grey[100],
            },
            h2: {
                fontFamily: ["system-ui", "-apple-system", "BlinkMacSystemFont", '"Segoe UI"', "sans-serif"].join(","),
                fontSize: 20,
                fontWeight: 700,
                color: colors.grey[100],
            },
            h3: {
                fontFamily: ["system-ui", "-apple-system", "BlinkMacSystemFont", '"Segoe UI"', "sans-serif"].join(","),
                fontSize: 16,
                fontWeight: 700,
                color: colors.grey[100],
            },
            h4: {
                fontFamily: ["system-ui", "-apple-system", "BlinkMacSystemFont", '"Segoe UI"', "sans-serif"].join(","),
                fontSize: 14,
                fontWeight: 700,
                color: colors.grey[100],
            },
            h5: {
                fontFamily: ["system-ui", "-apple-system", "BlinkMacSystemFont", '"Segoe UI"', "sans-serif"].join(","),
                fontSize: 12,
                fontWeight: 700,
                color: colors.grey[100],
            },
            h6: {
                fontFamily: ["system-ui", "-apple-system", "BlinkMacSystemFont", '"Segoe UI"', "sans-serif"].join(","),
                fontSize: 11,
                fontWeight: 700,
                color: colors.grey[100],
            },
        },
        components: {
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        background: mode === "dark"
                            ? "linear-gradient(160deg, #161a29 0%, #0f121d 100%)"
                            : "linear-gradient(160deg, #ffffff 0%, #f8fafc 100%)",
                        color: mode === "dark" ? colors.grey[100] : colors.grey[200],
                        border: `1px solid ${colors.grey[500]}`,
                        boxShadow: mode === "dark"
                            ? "0 10px 28px rgba(0, 0, 0, 0.75), 0 0 14px rgba(130, 170, 255, 0.14)"
                            : "0 8px 20px rgba(0, 0, 0, 0.12)",
                        fontSize: "11px",
                        fontWeight: 600,
                        letterSpacing: "0.3px",
                        padding: "5px 10px",
                        borderRadius: "7px",
                        backdropFilter: "blur(10px)",
                    },
                    arrow: {
                        color: mode === "dark" ? "#161a29" : "#ffffff",
                        "&:before": {
                            border: `1px solid ${colors.grey[500]}`,
                        },
                    },
                },
            },
            MuiCssBaseline: {
                styleOverrides: {
                    body: css`
                        * {
                            box-sizing: border-box;
                        }
                        ${transitionEnabled ? 'transition: background-color 0.5s, color 0.5s;' : ''}
                        background: ${colors.background.body};
                        color: ${colors.grey[100]};
                        min-height: 100vh;
                        display: flex;
                        align-items: stretch;
                        justify-content: center;
                        margin: 0;
                    `,
                },
            },
        },

    };
};

export const ColorModeContext = createContext({
    toggleColorMode: () => { },
});

export const useMode = () => {
    const [mode, setMode] = useState(() => {
        const storedMode = localStorage.getItem("themeMode");
        return storedMode ? storedMode : "dark";
    });

    const [transitionEnabled, setTransitionEnabled] = useState(false);

    const colorMode = useMemo(
        () => ({
            toggleColorMode: () => {
                setTransitionEnabled(true);
                setMode((prev) => {
                    const newMode = prev === "light" ? "dark" : "light";
                    localStorage.setItem("themeMode", newMode);
                    return newMode;
                });
            },
        }),
        []
    );

    const theme = useMemo(() => createTheme(themeSettings(mode, transitionEnabled)), [mode, transitionEnabled]);

    useEffect(() => {
        if (transitionEnabled) {
            const timer = setTimeout(() => {
                setTransitionEnabled(false);
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [transitionEnabled]);

    return [theme, colorMode, transitionEnabled];
};